/**
 * ============================================================================
 * TABS COMPONENT - 100% Ready
 * ============================================================================
 */

import React from 'react';

/**
 * Tabs Component
 * @param {Object} props
 * @param {Array} props.tabs - Array of { id, label, icon, count }
 * @param {string} props.activeTab - Active tab ID
 * @param {Function} props.onTabChange - Tab change handler
 * @param {string} props.variant - Variant (default, pills, underline)
 * @param {string} props.className - Additional classes
 * @returns {JSX.Element}
 */
const Tabs = ({
  tabs = [],
  activeTab,
  onTabChange,
  variant = 'default',
  className = '',
}) => {
  const variantClasses = {
    default: 'border-b border-gray-200 dark:border-gray-700',
    pills: 'bg-gray-100 dark:bg-gray-800 p-1 rounded-xl',
    'pills-green': 'bg-[#111] border border-white/10 p-1 rounded-xl',
    underline: 'border-b-2 border-gray-200 dark:border-gray-700',
  };

  const tabClasses = {
    default: (isActive) =>
      `px-4 py-2 font-semibold text-sm transition-colors ${
        isActive
          ? 'text-blue-600 dark:text-blue-400 border-b-2 border-blue-600 dark:border-blue-400'
          : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
      }`,
    pills: (isActive) =>
      `px-4 py-2 rounded-lg font-semibold text-sm transition-all ${
        isActive
          ? 'bg-white dark:bg-gray-700 text-blue-600 dark:text-blue-400 shadow-md'
          : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
      }`,
    'pills-green': (isActive) =>
      `px-4 py-2 rounded-lg font-semibold text-sm transition-all ${
        isActive
          ? 'bg-[#22c55e] text-[#0a0a0a] shadow-md hover:bg-[#22c55e] hover:text-[#0a0a0a] active:bg-[#16a34a] active:text-[#0a0a0a] focus:outline-none focus:ring-2 focus:ring-[#22c55e] focus:ring-offset-2 focus:ring-offset-[#111]'
          : 'text-[#22c55e] border border-[#22c55e]/50 hover:bg-[#22c55e]/25 hover:text-[#22c55e] hover:border-[#22c55e] active:bg-[#22c55e]/35 active:text-[#22c55e] focus:outline-none focus:ring-2 focus:ring-[#22c55e] focus:ring-offset-2 focus:ring-offset-[#111]'
      }`,
    underline: (isActive) =>
      `px-4 py-2 font-semibold text-sm transition-colors ${
        isActive
          ? 'text-blue-600 dark:text-blue-400 border-b-2 border-blue-600 dark:border-blue-400'
          : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
      }`,
  };

  return (
    <div className={`${variantClasses[variant] || variantClasses.default} ${className}`}>
      <div className="flex gap-2 overflow-x-auto">
        {tabs.map((tab) => {
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => onTabChange?.(tab.id)}
              className={`
                ${tabClasses[variant]?.(isActive) || tabClasses.default(isActive)}
                flex items-center gap-2 whitespace-nowrap
                transition-all duration-200
                ${isActive ? 'transform scale-105' : ''}
              `}
            >
              {tab.icon && <span>{tab.icon}</span>}
              <span>{tab.label}</span>
              {tab.count !== undefined && tab.count > 0 && (
                <span
                  className={`
                    px-2 py-0.5 rounded-full text-xs font-bold
                    ${
                      variant === 'pills-green'
                        ? isActive
                          ? 'bg-[#0a0a0a]/30 text-[#0a0a0a]'
                          : 'bg-white/20 text-[#a3a3a3]'
                        : isActive
                        ? 'bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300'
                        : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                    }
                  `}
                >
                  {tab.count}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default Tabs;
